/* ⚛ REACT */
import { useState, useRef, useEffect } from "react";

/* 📦 LIBS */
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router";
import { useTranslation, Trans } from "react-i18next";

/* 🧩 COMPONENTS */
import { Input, InputPassword, Label, Button, Modal } from "../../Components";

/* 🔗 SERVICE */
import { authService } from "../../Services/authServices/requests.ts";
import { requestUser } from "../../Services/userServices/request.ts";

/* 🧠 REDUX */
import { store } from "../../Redux/Store";
import { SignIn, changeModal, ChangeCheckedTutorial } from "../../Redux/Actions";

/* ⚙ CONFIG*/
import config from "../../Config/config.json";

/* 📁 ASSETS*/
import { Logo_Mark, Lock, UserIcon } from "../../Assets/icons";
import logoBio from '../../Assets/Images/logoBio.png';

/* 🎨 STYLES */
import { Container, BlueContainer, Title, LoginCotaniner, LoginBox, Image, MobileGradiante, Wellcometext,
    ContainerForm, ImageMobile, CaptchaContainer, PasswordRecovery, DataPrivacy, ActionButtonsContainer,
    ForgotPasswordText, OutlineButton, LoginDivider } from "./styles";

/* 🔧 UTILS */
import { isValidField, ValidateField } from "../../util/validate";

const Login = () => {
    document.title = "Augen Login";
    
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation(); 
    
    const userName = useRef('');
    const userPassword = useRef('');
    const recaptchaValue = useRef();
    const recaptchaValueEmail = useRef();
    const userField = useRef();
    const passwordField = useRef();
    
    const emailValue = useRef('');
    const emailField = useRef('');
    
    // Variáveis de ambiente
    const gabLoginUrl = process.env.REACT_APP_GABLOGIN_URL;
    const googleLoginUrl = process.env.REACT_APP_GOOGLELOGIN_URL;
    const recaptchaKey = process.env.REACT_APP_RECAPTCHA_KEY;
    const hasCaptcha = Boolean(recaptchaKey);
    
    const [loginStarted, setLoginStarted] = useState(false);

    async function SendLogin(){
        ValidateField(userField.current)
        ValidateField(passwordField.current)
        try {
            userName.current = userField.current.getInputDOMNode().value;
            userPassword.current = passwordField.current.value;
        } catch (error) {
            console.log('Erro ao pegar os valores dos campos: '+error)
        }
        
        let captchaValue = null;
        let isCaptchaValid = true;

        if (hasCaptcha) {
            captchaValue = recaptchaValue.current?.getValue();
            if (!captchaValue) isCaptchaValid = false;
        }

        if(!isCaptchaValid){
            toast(t("message.login.robot"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }else if(isValidField(userName.current) && isValidField(userPassword.current)){
            try{
                setLoginStarted(true);
                if (hasCaptcha) recaptchaValue.current?.reset(); 
                
                await authService.SignIn(userName.current, userPassword.current, captchaValue, navigator?.userAgent);
                const newUser = await requestUser.getCurrentUser();

                if (newUser?.user?.lang) {
                    i18n.changeLanguage(newUser.user.lang);
                }

                if(newUser?.data?.message){
                    toast(newUser.data.message, {
                        position: "top-center",
                        type: 'error',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: false,
                        draggable: false,
                        progress: undefined,
                        theme: config.theme,
                    });
                }else{
                    store.dispatch(SignIn(newUser));
                    store.dispatch(ChangeCheckedTutorial(newUser.user.checkedTutorial));
                    const from = location.state?.from || "/";
                    setTimeout(() => {
                        navigate(from);
                    }, 20);
                }
            }catch(error) {
                toast(error.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                    theme: config.theme,
                });
            }finally{
                setLoginStarted(false);
            }
        }else{
            toast(t("message.login.fill"), {
                position: "top-center",
                type: 'error',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }
    }

    async function SendEmail(){
        let captchaValue = null;
        let isCaptchaValid = true;

        if (hasCaptcha) {
            captchaValue = recaptchaValueEmail.current?.getValue();
            if (!captchaValue) isCaptchaValid = false;
        }

        if(!isCaptchaValid){
            toast(t("message.login.robot"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
            return;
        }else if(isValidField(emailValue.current)){
            try {
                if (hasCaptcha) recaptchaValueEmail.current?.reset();
                store.dispatch(changeModal(''))
                const response = await authService.ForgetPassWrod(emailValue.current, captchaValue);
                toast(response.message, {
                    position: "top-center",
                    type: 'success',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                });
            } catch (error) {
                toast(error.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                });
            }
        }else{
            toast(t("message.save"), {
                position: "top-center",
                type: 'error',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }
    }

    // Processa os logins externos (Google ou GAB)
    async function ProcessExternalLogin(code) {
        try {
            setLoginStarted(true);
            
            await authService.SignInWithGabCode(code);
            const newUser = await requestUser.getCurrentUser();

            if (newUser?.user?.lang) {
                i18n.changeLanguage(newUser.user.lang);
            }

            if(newUser?.data?.message){
                toast(newUser.data.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    theme: config.theme,
                });
            } else {
                store.dispatch(SignIn(newUser));
                store.dispatch(ChangeCheckedTutorial(newUser?.user?.checkedTutorial));
                const from = location.state?.from || "/";
                setTimeout(() => {
                    navigate(from);
                }, 20);
            }
        } catch(error) {
            toast(error.message, {
                position: "top-center",
                type: 'error',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                theme: config.theme,
            });
        } finally {
            setLoginStarted(false);
        }
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get("code");
        const error = queryParams.get("error");
        const errorDescription = queryParams.get("error_description");

        if (error || errorDescription) {
            let errorMessage = errorDescription 
                ? errorDescription.replace(/\+/g, ' ') 
                : t("message.login.error") || "Ocorreu um erro durante a autenticação.";

            errorMessage = errorMessage.replace("PreSignUp failed with error ", "");

            toast(errorMessage, {
                position: "top-center",
                type: 'error',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                theme: config.theme,
            });

            window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (code) {
            if (window.opener) {
                window.opener.postMessage({ type: 'EXTERNAL_LOGIN_CODE', code: code }, window.location.origin);
                window.close(); 
            } else {
                window.history.replaceState({}, document.title, window.location.pathname);
                ProcessExternalLogin(code);
            }
        }

        const handleMessage = (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && event.data.type === 'EXTERNAL_LOGIN_CODE') {
                const receivedCode = event.data.code;
                ProcessExternalLogin(receivedCode);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [location.search]);

    return(
        <>
            <Container>
                <BlueContainer>
                    <Title>
                        <Trans i18nKey="login.title" components={{ br: <br /> }} />
                    </Title>
                </BlueContainer>
                <LoginCotaniner>
                    <MobileGradiante>
                        <LoginBox>
                            <ContainerForm>
                                <Image src={logoBio}/>
                                <Wellcometext>{t("login.welcome-message")}</Wellcometext>

                                <ActionButtonsContainer>
                                    <OutlineButton 
                                        disabled={!googleLoginUrl} 
                                        onClick={() => {
                                            if (googleLoginUrl) {
                                                window.location.href = googleLoginUrl;
                                            }
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fill="#FFC107" d="M43.611 20.083H42V20H24V28H35.303C33.655 32.613 29.231 36 24 36C17.373 36 12 30.627 12 24C12 17.373 17.373 12 24 12C27.059 12 29.845 13.153 31.961 15.039L37.618 9.382C34.054 6.136 29.28 4 24 4C12.955 4 4 12.955 4 24C4 35.045 12.955 44 24 44C35.045 44 44 35.045 44 24C44 22.659 43.862 21.35 43.611 20.083Z"/>
                                            <path fill="#FF3D00" d="M6.306 14.691L12.441 19.191C13.985 15.688 17.618 13.2 21.961 13.2C24.015 13.2 25.918 13.882 27.462 15.039L33.118 9.382C30.285 6.786 26.386 5.2 21.961 5.2C15.059 5.2 9.07 9.256 6.306 14.691Z"/>
                                            <path fill="#4CAF50" d="M24 44C29.414 44 34.28 42.064 37.95 38.868L31.782 33.653C29.622 35.313 26.961 36.316 24 36.316C18.455 36.316 13.784 32.684 12.28 27.764L6.113 32.493C8.948 38.163 15.895 44 24 44Z"/>
                                            <path fill="#1976D2" d="M43.611 20.083H42V20H24V28H35.303C34.526 30.15 33.11 32.062 31.332 33.456L37.5 38.671C38.077 38.136 44 33.545 44 24C44 22.659 43.862 21.35 43.611 20.083Z"/>
                                        </svg>
                                        Continuar com Google
                                    </OutlineButton>

                                    <OutlineButton 
                                        disabled={!gabLoginUrl} 
                                        onClick={() => {
                                            if (gabLoginUrl) {
                                                window.location.href = gabLoginUrl;
                                            }
                                        }} 
                                    >
                                        Continuar com GAB
                                    </OutlineButton>
                                </ActionButtonsContainer>

                                <LoginDivider>ou</LoginDivider>

                                <Label text={t("inputs.email-user.label")} aling="flex-start"/>
                                <Input 
                                    id='email' 
                                    name='email' 
                                    type="text" 
                                    innerRef={userField}
                                    invalidMessage = {t("inputs.email-user.invalid-message")} 
                                    value={userName.current}
                                    icon= {UserIcon}
                                    validationFunction = {(text) => isValidField(text)}
                                    onchange={(text) => userName.current = text}> 
                                </Input>

                                <Label text={t("inputs.password.label")} aling="flex-start"/>
                                <InputPassword 
                                    id='password' 
                                    name='password' 
                                    innerRef={passwordField}
                                    invalidMessage = {t("login.password.invalid-message")} 
                                    value={userPassword.current}
                                    icon= {Lock}
                                    validationFunction = {(text) => isValidField(text)}
                                    onchange={(text) => userPassword.current = text}>
                                </InputPassword>

                                {hasCaptcha && (
                                    <CaptchaContainer>
                                        <ReCAPTCHA ref={recaptchaValue} sitekey={recaptchaKey} theme={config.theme} hl={t("login.ReCAPTCHA")}/>
                                    </CaptchaContainer>
                                )}

                                <DataPrivacy href="https://www.biosolvit.com/politica-de-privacidade" target="blank">{t("buttons.data-privacy")}</DataPrivacy>
                                
                                <ActionButtonsContainer>
                                    <Button 
                                        text={loginStarted ? t("buttons.entering") : t("buttons.enter")} 
                                        disabled={loginStarted ? true : false} 
                                        width={"100%"} 
                                        onClick={() => SendLogin()} 
                                    />
                                </ActionButtonsContainer>

                                <PasswordRecovery onClick={()=> store.dispatch(changeModal('ForgetPassword'))}>
                                    <ForgotPasswordText>
                                        <Trans i18nKey="login.forgot-password" components={{ b: <b /> }} />
                                    </ForgotPasswordText>
                                </PasswordRecovery>
                                
                                <ImageMobile src={Logo_Mark}/>
                            </ContainerForm>
                        </LoginBox>
                    </MobileGradiante>
                </LoginCotaniner>
            </Container>

            <Modal title={t("modal.forgot-password.title")} name={'ForgetPassword'} width={'60%'} height={'60%'}>
                <Label text={t("inputs.email-user.label")} aling="flex-start"/>
                <Input 
                    id='email' 
                    name='email' 
                    innerRef={emailField}
                    placeholder= {t("inputs.email-user.placeholder")}
                    invalidMessage = {t("inputs.email-user.invalid-message")}
                    validationFunction = {(text) => isValidField(text)}
                    onchange={(text) => emailValue.current = text}>
                </Input>

                {hasCaptcha && (
                    <CaptchaContainer>
                        <ReCAPTCHA ref={recaptchaValueEmail} sitekey={recaptchaKey} theme={config.theme} hl={t("login.ReCAPTCHA")}/>
                    </CaptchaContainer>
                )}

                <Button text={t("buttons.submit")} width={'120px'} onClick={() => SendEmail()}/>
            </Modal>

        </>
    )
}

export default Login;